import React, { useState } from 'react';
import { AccountsStatement } from '../../../../networking/accounts/statements/AccountsStatement';
import { Stack, Button, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import {
  stableSort,
  getComparator,
  Order,
  convertToListOfLabel
} from '../../../../components/TPOTable/TPOTableFunctions';
import { currencyFormatting, rateFormatting } from '../../../../common/helpers';
import TPOTable, { Header } from '../../../../components/TPOTable/TPOTable';
import TPOExportButton from '../../../../components/TPOExportButton/TPOExportButton';
import { VERIBANC_RATINGS_DROPDOWN_TESTID } from '../AccountGraph/AccountGraph';
import { InstitutionRating } from '../../../../networking/accounts/ratings/InstitutionRating';
import {
  getCsvRatingFileRows
} from '../../../../utils/accounts/csvExportUtils';

export const ACCOUNTS_PAGE_DROPDOWN_TESTID = 'ACCOUNTS_PAGE_DROPDOWN_TESTID';

export interface AccountTableRow {
  account: string;
  financialInstitution: string;
  type: string;
  balanceAsOf: string;
  lastBalance: number;
  monthlyAverage: number;
  monthlyInterest: number;
  interestRate: number;
  status: string;
}

interface AccountTableProps {
  statements: AccountsStatement[];
  ratings: InstitutionRating[];
}

const AccountTable = (props: AccountTableProps) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof AccountTableRow>('account');
  const [isExpanded, setIsExpanded] = useState(false);

  const accountTableHeaders: Header[] = [
    {
      label: t('listOfAccounts.accountTableAccountHeader'),
      numeric: false,
      id: 'account',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableFinancialInsititutionHeader'),
      numeric: false,
      id: 'financialInstitution',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableTypeHeader'),
      numeric: false,
      id: 'type',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableBalanceHeader'),
      numeric: false,
      id: 'balanceAsOf',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableLastBalanceHeader'),
      numeric: true,
      id: 'lastBalance',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableMonthlyAverageHeader'),
      numeric: true,
      id: 'monthlyAverage',
      showToolTip: false
    },
    {
      label: t('listOfAccounts.accountTableMonthlyInterestHeader'),
      numeric: true,
      id: 'monthlyInterest',
      showToolTip: true,
      toolTipHeader: t('listOfAccounts.monthlyRateToolTipTitle'),
      toolTipText: t('listOfAccounts.monthlyRateToolTipContent')
    },
    {
      label: t('listOfAccounts.accountTableInterestRateHeader'),
      numeric: true,
      id: 'interestRate',
      showToolTip: true,
      toolTipHeader: t('listOfAccounts.interestRateToolTipTitle'),
      toolTipText: t('listOfAccounts.interestRateToolTipContent')
    },
    {
      label: t('listOfAccounts.accountTableAccountStatus'),
      numeric: false,
      id: 'status',
      showToolTip: false
    }
  ];

  const createData = (statements: AccountsStatement[]): AccountTableRow[] => {
    return statements.map((statement) => {
      return {
        account: `${statement.accountName} - ${statement.accountNumber}`,
        financialInstitution: statement.institutionName,
        type: statement.accountType,
        balanceAsOf: statement.balanceAsOf,
        lastBalance: statement.lastBalance,
        monthlyAverage: statement.monthlyAverage,
        monthlyInterest: statement.monthlyInterest,
        interestRate: statement.interestRate,
        status: statement.isClosed ? 'Closed' : 'Open'
      };
    });
  };

  const rows = createData(props.statements);

  const sortedRows = stableSort(rows, getComparator(order, orderBy));

  const getDataPoints = (obj: AccountTableRow) => {
    // converts object to list of strings
    return [
      obj.account,
      obj.financialInstitution,
      obj.type,
      obj.balanceAsOf && obj.balanceAsOf !== '0' ? new Date(obj.balanceAsOf).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }) : '',
      currencyFormatting(Number(obj.lastBalance), 2),
      currencyFormatting(Number(obj.monthlyAverage), 2),
      currencyFormatting(Number(obj.monthlyInterest), 2),
      rateFormatting(Number(obj.interestRate) * 100),
      obj.status
    ];
  };

  const formattedTableRows = sortedRows.map((row: AccountTableRow) => getDataPoints(row));

  return (
    <Stack
      spacing={2}
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <TPOExportButton
          sheetContent={(() => {
            // Filter ratings to only include client's institutions
            const clientInstitutions = props.statements.map(s => s.institutionName);
            const clientRatings = props.ratings.filter(r =>
              clientInstitutions.includes(r.institutionName)
            );

            return getCsvRatingFileRows(
              clientRatings,
              t('listOfAccounts.ratings.csvHeader'),
              t('listOfAccounts.ratings.csvFdicText'),
              t('listOfAccounts.ratings.csvLastUpdateHeader')
            );
          })()}
          fileName={'VeribancRatings'}
          label={t('listOfAccounts.ratings.csvExport')}
          width={'180px'}
          dropDownTranslation={'62px'}
          data-testid={VERIBANC_RATINGS_DROPDOWN_TESTID}
        />
        <TPOExportButton
          sheetContent={formattedTableRows}
          sheetHeaders={convertToListOfLabel(accountTableHeaders)}
          fileName={'AccountTable'}
          label={t('listOfAccounts.exportAccountData')}
          width={'180px'}
          dropDownTranslation={'62px'}
          data-testid={ACCOUNTS_PAGE_DROPDOWN_TESTID}
        />
      </Box>
      <Box sx={{ width: '100%' }}>
        <TPOTable
          headers={accountTableHeaders}
          content={formattedTableRows}
          maxHeight={!isExpanded && formattedTableRows.length > 10 ? 600 : '80vh'}
          sortable={{
            order: order,
            setOrder: setOrder,
            orderBy: orderBy,
            setOrderBy: setOrderBy
          }}
        />
        {formattedTableRows.length > 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              {isExpanded ? t('listOfAccounts.viewLess', 'View less') : t('listOfAccounts.viewAllAccounts', 'View all accounts')}
            </Button>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default AccountTable;
